import { extendZodWithOpenApi } from "zod-openapi";
extendZodWithOpenApi(z);

import { z } from "zod";
import {
  BlockBaseSchema,
  BlockIdPlaceholderSchema,
  FilterSchema,
} from "block-system/types/base";
import { blockType } from "./constants";
import { WidthSchema } from "../__shared__/components/WidthField/schema";
import { HorizontalAlignmentSchema } from "../__shared__/components/AlignmentField/schema";
import { RecordOrderSchema } from "server/schemas/table-records";
import { TableIdSchema } from "@/server/schemas/table-tables";
import {
  SUPPORTED_KANBAN_BLOCK_DESCRIPTION_FIELD_TYPES,
  SUPPORTED_KANBAN_BLOCK_GROUP_FIELD_TYPES,
  SUPPORTED_KANBAN_BLOCK_TITLE_FIELD_TYPES,
  SUPPORTED_KANBAN_BLOCK_FIELD_TYPES,
} from "./Block/constants-and-types";
import type { APIFieldType } from "@/server/services/tables/types/fields";
import { TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME } from "@/server/schemas/table-fields";

export const KanbanColumnSchema = z.object({
  value: z.any(),
  label: z.string(),
});

export const KanbanBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: z.object({
    id: z.string().cuid().or(BlockIdPlaceholderSchema).optional(),
    title: z.string().optional(),
    tableId: TableIdSchema.optional().describe(
      "The ID of the Zapier Table connected to the block."
    ),

    cardTitleFieldId: z
      .number()
      .or(z.string())
      .optional()
      .describe(
        `The id of the field to use for the Kanban Card Title.
        Allowed field types are: ${formatFieldTypes({
          fieldTypes: SUPPORTED_KANBAN_BLOCK_TITLE_FIELD_TYPES,
        })}`
      ),
    groupFieldId: z
      .number()
      .or(z.string())
      .optional()
      .describe(
        `The id of the field to use for the Kanban Column Group. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: SUPPORTED_KANBAN_BLOCK_GROUP_FIELD_TYPES,
          }
        )}`
      ),

    cardDescriptionFieldId: z
      .number()
      .or(z.string())
      .optional()
      .describe(
        `The id of the field to use for the Kanban Card Description. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: SUPPORTED_KANBAN_BLOCK_DESCRIPTION_FIELD_TYPES,
          }
        )}`
      ),

    cardDetailFields: z
      .array(z.number().or(z.string()))
      .optional()
      .describe(
        `The ids of the fields to use for the Kanban Card Details. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: SUPPORTED_KANBAN_BLOCK_FIELD_TYPES,
          }
        )}`
      ),

    selectedColumns: KanbanColumnSchema.array()
      .optional()
      .describe(
        "The columns to display in the Kanban board. Each column has a value and a label. When using Zapier Tables labeled_string fields, the columns are defined by the static_choices."
      ),
    canCreateCards: z.boolean().default(false),
    newCardButtonCopy: z.string().optional(),
    canUpdateCards: z.boolean().default(false),
    canDeleteCards: z.boolean().default(false),
    canMoveCards: z.boolean().default(true),
    canMoveColumns: z.boolean().default(false),
    style: z
      .object({
        width: WidthSchema.optional(),
        alignment: HorizontalAlignmentSchema.optional(),
      })
      .optional(),
    filters: z.array(FilterSchema).optional(),
    orders: z.array(RecordOrderSchema).optional(),
  }),
  stateId: z.string().cuid().optional(),
}).openapi({ ref: "KanbanBlock" });

function formatFieldTypes({ fieldTypes }: { fieldTypes: APIFieldType[] }) {
  return fieldTypes
    .map((fieldType) => {
      return `${TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME[fieldType]} (${fieldType})`;
    })
    .join(", ");
}
